"use client";

import React, { useState } from "react";
import Image from "next/image";
import { scheduleData } from "@/lib/utils";
import { LevelAccordionProps, SessionRowProps } from "@/types/caira";

// "2 CPE Credits" -> "2 CPE each"
const toCpeEach = (credits?: string) =>
  credits ? credits.replace(/credits?$/i, "each").trim() : "";

// "7:00 PM - 9:00 PM" -> "7 PM"
const toShortTime = (time?: string) =>
  time ? time.split("-")[0].trim().replace(":00", "") : "";

const CairaScheduleNew: React.FC = () => {
  const [expandedLevel, setExpandedLevel] = useState<string>("I");

  const toggleLevel = (level: string) => {
    setExpandedLevel(expandedLevel === level ? "" : level);
  };

  return (
    <section className="pt-10 md:pr-0 pr-4">
      <div className="md:w-[1004px] w-full mx-auto px-0 md:px-6">
        <div className="flex flex-col gap-3 md:gap-4">
          {scheduleData?.map((levelData, index) => (
            <LevelAccordion
              key={levelData.level}
              data={levelData}
              isExpanded={expandedLevel === levelData.level}
              onToggle={() => toggleLevel(levelData.level)}
              isLast={index === scheduleData.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const LevelAccordion: React.FC<LevelAccordionProps> = ({
  data,
  isExpanded,
  onToggle,
}) => {
  const hasContent = data.sessions.length > 0;
  const cpeEach = toCpeEach(data.sessions[0]?.credits);

  return (
    <div
      className="overflow-hidden"
      style={{
        borderRadius: "8px",
        border: "0.82px solid rgba(255, 255, 255, 0.06)",
        background: "rgba(33, 100, 167, 0.10)",
        boxShadow: "0 0 45.018px 0 rgba(166, 137, 255, 0.30) inset",
        backdropFilter: "blur(4.920000076293945px)",
        WebkitBackdropFilter: "blur(4.920000076293945px)", // Safari support
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasContent}
        className={`w-full flex items-center justify-between gap-3 px-[10px] md:px-[10px] py-[8px] md:py-[8px] bg-gradient-to-r from-[#12203f] to-[#241a3f] transition-colors ${
          hasContent ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <span
          className={`text-sm md:text-base font-bold ${
            isExpanded ? "text-[#3399FF]" : "text-[#3399FF]"
          }`}
        >
          {data.title}
        </span>
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          {cpeEach && (
            <span className="text-white/70 text-[11px] md:text-sm whitespace-nowrap">
              {cpeEach}
            </span>
          )}
          <svg
            className={`w-4 h-4 md:w-5 md:h-5 text-white/80 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Accordion Content */}
      {isExpanded && hasContent && (
        <div>
          {data.sessions?.map((session, idx) => (
            <SessionRow
              key={idx}
              session={session}
              isLast={idx === data.sessions.length - 1}
              index={idx}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SessionRow: React.FC<SessionRowProps> = ({ session, isLast, index }) => {
  const bgColor =
    index % 2 === 0 ? "rgba(255, 255, 255, 0.00)" : "rgba(255, 255, 255, 0.10)";
  // The "|" is a separator, so it only earns its place when there is something
  // on both sides of it. One value alone renders bare; neither renders nothing.
  const schedule = [session.date?.trim(), toShortTime(session.time)].filter(
    Boolean,
  );
  return (
    <div
      className={`flex items-center justify-between gap-3 px-[10px] md:px-[10px] py-[8px] md:py-[8px] ${
        !isLast ? "" : ""
      }`}
      style={{ background: bgColor }}
    >
      {/* Icon + title */}
      <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
        <div className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0">
          {session.icon ? (
            <div className="relative w-5 h-5 md:w-6 md:h-6 rounded-full overflow-hidden">
              <Image src={session.icon} alt="" fill className="object-cover" />
            </div>
          ) : (
            <div className="w-5 h-5 md:w-6 md:h-6" />
          )}
        </div>
        <p className="text-white text-[12px] md:text-sm leading-tight font-medium">
          {session.title}
        </p>
      </div>

      {/* Date | time */}
      {schedule.length > 0 && (
        <div className="flex-shrink-0">
          <span className="text-[#FFBA08] text-[11px] md:text-sm font-medium whitespace-nowrap">
            {schedule.join(" | ")}
          </span>
        </div>
      )}
    </div>
  );
};

export default CairaScheduleNew;
